import { createRoute, redirect } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { authenticatedRoute } from '@/authenticated-route';
import FullPageLoadingSpinner from '@/components/ui/full-page-loading-spinner';
import {
  PERMISSION_LAYOUT_ROUTE_ID,
  PERMISSION_ROUTES,
} from '@/features/permission/consts/routes';
import {
  permissionGroupSearchSchema,
  toPermissionGroupListParams,
} from '@/features/permission/types/permission-group';
import { beforeLoadRouterGuard } from '@/integrations/tanstack-router/before-load-router-guard';
import { getSuspenseQueryPermissionGroupListOptions } from './services/use-query-permission-group-list';
import { accountPermissionSearchSchema } from './types/account-permission';

const permissionLayoutRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  id: PERMISSION_LAYOUT_ROUTE_ID,
  // TODO: add permission route guard
  beforeLoad: beforeLoadRouterGuard([], {
    featureFlag: 'userManagementEnabled',
  }),
});

const accountPermissionRoute = createRoute({
  getParentRoute: () => permissionLayoutRoute,
  path: PERMISSION_ROUTES.accounts,
  validateSearch: zodValidator(accountPermissionSearchSchema),
  beforeLoad: beforeLoadRouterGuard([], {
    featureFlag: 'accountPermissionEnabled',
  }),
  pendingComponent: FullPageLoadingSpinner,
}).lazy(() => import('./account-permission').then(({ Route }) => Route));

const batchAssignPermissionRoute = createRoute({
  getParentRoute: () => permissionLayoutRoute,
  path: PERMISSION_ROUTES.batchAssign,
  beforeLoad: beforeLoadRouterGuard([], {
    featureFlag: 'batchAssignPermissionEnabled',
  }),
  pendingComponent: FullPageLoadingSpinner,
}).lazy(() => import('./batch-assign-permission').then(({ Route }) => Route));

const permissionGroupRoute = createRoute({
  getParentRoute: () => permissionLayoutRoute,
  path: PERMISSION_ROUTES.groups,
  validateSearch: zodValidator(permissionGroupSearchSchema),
  pendingComponent: FullPageLoadingSpinner,
  loaderDeps: ({ search }) => ({
    keyword: search.keyword,
    status: search.status,
  }),
  loader: ({ deps, context: { queryClient } }) =>
    queryClient.ensureQueryData(
      getSuspenseQueryPermissionGroupListOptions(
        toPermissionGroupListParams(deps),
      ),
    ),
}).lazy(() => import('./permission-group').then(({ Route }) => Route));

const permissionIndexRoute = createRoute({
  getParentRoute: () => permissionLayoutRoute,
  path: PERMISSION_ROUTES.index,
  beforeLoad: () => {
    throw redirect({ to: PERMISSION_ROUTES.groups });
  },
});

export const permissionRouteTree = permissionLayoutRoute.addChildren([
  permissionIndexRoute,
  accountPermissionRoute,
  batchAssignPermissionRoute,
  permissionGroupRoute,
]);
